import React from 'react'
import styled from 'styled-components'

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 50%;
  height: 100vh;
  background: var(--color-background);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 1000;
  overflow-y: auto;
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 999;
  opacity: ${props => props.$isOpen ? '1' : '0'};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-text);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`

const Header = styled.div`
  margin-bottom: 2rem;
`

const Title = styled.h2`
  font-size: 2rem;
  color: var(--color-text);
  margin-bottom: 1rem;
`

const Status = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  background: ${props => {
    switch (props.$status) {
      case 'active': return 'rgba(59, 130, 246, 0.2)'
      case 'completed': return 'rgba(16, 185, 129, 0.2)'
      case 'draft': return 'rgba(249, 115, 22, 0.2)'
      default: return 'rgba(255, 255, 255, 0.1)'
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'active': return 'var(--color-primary)'
      case 'completed': return 'var(--color-secondary)'
      case 'draft': return 'var(--color-accent)'
      default: return 'var(--color-text)'
    }
  }};
`

const Section = styled.section`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`

const AgentList = styled.div`
  display: grid;
  gap: 1rem;
`

const AgentCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`

const AgentIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => {
    switch (props.$type) {
      case 'coder': return 'rgba(59, 130, 246, 0.2)'
      case 'artist': return 'rgba(16, 185, 129, 0.2)'
      case 'social': return 'rgba(249, 115, 22, 0.2)'
      default: return 'rgba(255, 255, 255, 0.1)'
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => {
    switch (props.$type) {
      case 'coder': return 'var(--color-primary)'
      case 'artist': return 'var(--color-secondary)'
      case 'social': return 'var(--color-accent)'
      default: return 'var(--color-text)'
    }
  }};
`

const AgentInfo = styled.div`
  flex: 1;

  h4 {
    color: var(--color-text);
    margin-bottom: 0.25rem;
  }

  p {
    color: var(--color-text-secondary);
    font-size: 0.875rem;
  }
`

const ActivityLog = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const LogEntry = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`

const LogTime = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  white-space: nowrap;
`

const LogMessage = styled.div`
  color: var(--color-text);
  font-size: 0.875rem;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
    color: white;
    
    &:hover {
      transform: translateY(-2px);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: var(--color-text);
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`

function ProjectDetails({ project, isOpen, onClose, onAction }) {
  if (!project) return null

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <Panel $isOpen={isOpen}>
        <CloseButton onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </CloseButton>

        <Header>
          <Title>{project.name}</Title>
          <Status $status={project.status}>
            {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
          </Status>
        </Header>

        <Section>
          <SectionTitle>Overview</SectionTitle>
          <p>{project.description}</p>
        </Section>

        <Section>
          <SectionTitle>Assigned Agents</SectionTitle>
          <AgentList>
            {project.activeAgents?.map((agent, index) => (
              <AgentCard key={index}>
                <AgentIcon $type={agent.type}>
                  {agent.type.charAt(0).toUpperCase()}
                </AgentIcon>
                <AgentInfo>
                  <h4>{agent.name}</h4>
                  <p>{agent.currentTask}</p>
                </AgentInfo>
              </AgentCard>
            ))}
          </AgentList>
        </Section>

        <Section>
          <SectionTitle>Activity Log</SectionTitle>
          <ActivityLog>
            {project.activityLog?.map((log, index) => (
              <LogEntry key={index}>
                <LogTime>{formatTime(log.timestamp)}</LogTime>
                <LogMessage>{log.message}</LogMessage>
              </LogEntry>
            ))}
          </ActivityLog>
        </Section>

        <ActionButtons>
          {project.status === 'active' && (
            <>
              <Button onClick={() => onAction('pause')}>Pause Project</Button>
              <Button $primary onClick={() => onAction('view-output')}>
                View Output
              </Button>
            </>
          )}
          {project.status === 'completed' && (
            <>
              <Button onClick={() => onAction('archive')}>Archive</Button>
              <Button $primary onClick={() => onAction('duplicate')}>
                Duplicate Project
              </Button>
            </>
          )}
          {project.status === 'draft' && (
            <>
              <Button onClick={() => onAction('delete')}>Delete</Button>
              <Button $primary onClick={() => onAction('deploy')}>
                Deploy Project
              </Button>
            </>
          )}
        </ActionButtons>
      </Panel>
    </>
  )
}

export default ProjectDetails
